import { useRef, useState } from 'react';
import { t } from '@lingui/macro';

import CaretDown from '@decisiv/iconix/lib/components/CaretDown';
import Plus from '@decisiv/iconix/lib/components/Plus';
import Refresh from '@decisiv/iconix/lib/components/Refresh';
import Upload from '@decisiv/iconix/lib/components/Upload';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Menu from '@decisiv/ui-components/lib/components/Menu';
import Popover from '@decisiv/ui-components/lib/components/Popover';

import { MenuDivider } from './styles';

interface AddAssetMenuProps {
  disabled?: boolean;
  kind?: 'primary' | 'secondary';
  onSelectManual?: () => void;
  onSelectBulk?: () => void;
  /**
   * Switches the control to the "Manage Assets" variant: relabelled button,
   * plus a second menu below a divider. Omitted by the Accounts index header
   * button, which spans every account and so has none for a reset to target.
   */
  onSelectReset?: () => void;
}

export default function AddAssetMenu({
  disabled,
  kind = 'secondary',
  onSelectManual,
  onSelectBulk,
  onSelectReset,
}: AddAssetMenuProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);

  const hide = () => {
    setVisible(false);
  };

  const items = [
    {
      id: 'manual',
      text: t`Add Asset`,
      icon: Plus,
      disabled: !onSelectManual,
      onSelect: () => {
        hide();
        onSelectManual?.();
      },
    },
    {
      id: 'bulk',
      text: t`Bulk Add Assets`,
      icon: Upload,
      disabled: !onSelectBulk,
      onSelect: () => {
        hide();
        onSelectBulk?.();
      },
    },
  ];

  const resetItems = [
    {
      id: 'reset',
      text: t`Reset Depots Assets`,
      icon: Refresh,
      onSelect: () => {
        hide();
        onSelectReset?.();
      },
    },
  ];

  return (
    <>
      <Button
        text={onSelectReset ? t`Manage Assets` : t`Add Asset`}
        icon={CaretDown}
        iconPosition="right"
        size="small"
        kind={kind}
        disabled={disabled}
        onClick={() => setVisible((prev) => !prev)}
        ref={buttonRef}
      />
      <Popover
        placement="bottom-start"
        target={buttonRef}
        manageEvents={false}
        zIndex={200}
        visible={visible}
        onRequestHide={hide}
      >
        <Flex flexDirection="column">
          <Menu items={items} />
          {onSelectReset && (
            <>
              <MenuDivider />
              <Menu items={resetItems} />
            </>
          )}
        </Flex>
      </Popover>
    </>
  );
}
